import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { SEO, getServiceSchema, getFAQSchema } from '../../components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Shield, CheckCircle, ArrowRight, Phone, Heart, Lock } from 'lucide-react';

export const TraumaPTSDPage = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "Will I have to relive my trauma in therapy?",
      answer: "No, you won't be forced to relive traumatic experiences. While processing trauma is part of healing, we do this safely and at your pace. We'll first establish coping skills and ensure you feel safe before addressing difficult memories. You're always in control of what we discuss and when."
    },
    {
      question: "How long does trauma therapy take?",
      answer: "The duration varies based on the nature and severity of the trauma, as well as individual healing processes. Some people experience significant relief in 12-16 sessions, while complex trauma may require longer-term therapy. Healing isn't linear, and we'll work at a pace that feels right for you."
    },
    {
      question: "Is it too late to address trauma that happened years ago?",
      answer: "It's never too late. Trauma can affect you for years, even decades, but the brain has remarkable capacity for healing at any age. Many people successfully process old trauma and experience profound relief and life improvements, regardless of how long ago the trauma occurred."
    },
    {
      question: "Can trauma therapy be done through telehealth in Hawaii?",
      answer: "Yes. Telehealth trauma therapy is effective and widely used. Many clients find the comfort of their home environment helpful for trauma work. All evidence-based trauma treatments can be adapted for telehealth, and I'm experienced in providing trauma-informed care virtually throughout Hawaii."
    },
    {
      question: "What if I don't remember all the details of my trauma?",
      answer: "That's completely normal. Memory gaps are common with trauma. You don't need to remember every detail to heal. Trauma therapy focuses on addressing the symptoms and their impact on your current life, not necessarily recovering every memory. We work with whatever you do remember and how the trauma affects you now."
    },
    {
      question: "Do I need to confront the person who traumatized me?",
      answer: "No, confrontation is not necessary for healing. Trauma therapy focuses on your healing and recovery, not on others' involvement. Whether or not to have any contact with those who caused harm is entirely your choice, and we can explore what feels right and safe for you."
    }
  ];

  const serviceSchema = getServiceSchema(
    "Trauma & PTSD Therapy",
    "Compassionate trauma and PTSD treatment in Hawaii. Specialized trauma-informed care using evidence-based approaches to help you heal and reclaim your life.",
    "https://aloha-parents.preview.emergentagent.com/services/trauma-ptsd"
  );

  const faqSchema = getFAQSchema(faqs);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceSchema, faqSchema]
  };

  const traumaTypes = [
    "Childhood trauma and adverse experiences",
    "Physical, emotional, or sexual abuse",
    "Domestic violence",
    "Accidents and injuries",
    "Loss and grief",
    "Natural disasters",
    "Military combat experiences",
    "Witness to violence",
    "Medical trauma",
    "Complex trauma (repeated experiences)"
  ];

  const ptsdSymptoms = [
    "Intrusive memories or flashbacks",
    "Nightmares",
    "Avoidance of trauma reminders",
    "Negative changes in thoughts and mood",
    "Hypervigilance and being easily startled",
    "Difficulty sleeping",
    "Emotional numbness",
    "Difficulty concentrating"
  ];

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <SEO 
        title="Trauma & PTSD Therapy in Hawaii"
        description="Specialized trauma and PTSD treatment in Wailuku, HI. Compassionate, trauma-informed care to help you heal from traumatic experiences. Telehealth available."
        keywords="trauma therapy Hawaii, PTSD treatment Maui, trauma counseling Wailuku, PTSD therapist Hawaii, trauma informed care"
        canonicalUrl="https://aloha-parents.preview.emergentagent.com/services/trauma-ptsd"
        schemaData={combinedSchema}
      />
      
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="flex items-center text-sm text-gray-600">
          <button onClick={() => navigate('/')} className="hover:text-teal-600 transition-colors">
            Home
          </button>
          <ArrowRight className="w-4 h-4 mx-2" />
          <button onClick={() => navigate('/#services')} className="hover:text-teal-600 transition-colors">
            Services
          </button>
          <ArrowRight className="w-4 h-4 mx-2" />
          <span className="text-gray-900 font-medium">Trauma & PTSD</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-50 via-teal-50 to-white py-16 px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-emerald-600" />
              </div>
              <Badge className="mb-4 bg-emerald-100 text-emerald-800">Specialized Trauma Treatment</Badge>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Trauma & PTSD Therapy in Hawaii
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Healing from trauma is possible. With compassionate, trauma-informed care, I help 
                individuals process painful experiences, reduce PTSD symptoms, and reclaim their 
                sense of safety and well-being.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/contact')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg transition-all duration-200"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Begin Your Healing
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => window.location.href = 'tel:8084707650'}
                  className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-8 py-6 text-lg transition-all duration-200"
                >
                  Call (808) 470-7650
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&q=80" 
                alt="Person finding peace"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Compassionate Care for Trauma and PTSD
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                Trauma occurs when we experience or witness events that overwhelm our ability to cope. 
                Whether it happened recently or years ago, trauma can continue to affect your life, 
                relationships, and sense of safety. Post-Traumatic Stress Disorder (PTSD) is a natural 
                response to experiencing or witnessing traumatic events.
              </p>
              <p>
                As a Licensed Mental Health Counselor specializing in trauma, I understand that healing 
                is not about forgetting what happened—it's about processing the experience so it no longer 
                controls your life. My passion is bringing healing to people who have been through traumatic 
                or stressful experiences, and I'm committed to providing a safe, supportive environment for 
                your recovery.
              </p>
              <p>
                Using evidence-based, trauma-informed approaches, we'll work at your pace to help you 
                process difficult memories, reduce symptoms, and rebuild your sense of safety and 
                empowerment. You don't have to carry this burden alone—healing is possible, and I'm 
                here to support you every step of the way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trauma Types */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-800">Types of Trauma</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              We Can Help You Heal From
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Trauma takes many forms, and all experiences are valid
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {traumaTypes.map((type, index) => (
              <Card key={index} className="border-none shadow-lg bg-white hover:shadow-xl transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-start">
                    <Shield className="w-6 h-6 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <p className="text-gray-700 leading-relaxed">{type}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PTSD Symptoms */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-purple-100 text-purple-800">Recognizing PTSD</Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Understanding PTSD Symptoms
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                PTSD can develop after experiencing or witnessing a traumatic event. Common symptoms include:
              </p>
              <div className="space-y-4">
                {ptsdSymptoms.map((symptom, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-emerald-600 mr-4 mt-1 flex-shrink-0" />
                    <p className="text-gray-700 text-lg leading-relaxed">{symptom}</p>
                  </div>
                ))}
              </div>
              <p className="text-gray-600 mt-6 italic">
                If you're experiencing these symptoms, know that you're not alone and help is available.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=800&q=80" 
                alt="Finding peace"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Approach */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-green-100 text-green-800">Evidence-Based Treatment</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How We'll Work Together
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Trauma-informed, evidence-based approaches tailored to your healing journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-none shadow-lg bg-white">
              <CardHeader>
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-emerald-600" />
                </div>
                <CardTitle className="text-2xl">Trauma-Focused CBT</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-4">
                  A structured, evidence-based approach that helps you:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Process traumatic memories safely</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Challenge trauma-related beliefs</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Reduce PTSD symptoms</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Build coping skills</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-white">
              <CardHeader>
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-teal-600" />
                </div>
                <CardTitle className="text-2xl">Prolonged Exposure Therapy</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Gradually and safely confront trauma-related memories and situations:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Reduce avoidance behaviors</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Decrease emotional distress</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Regain sense of control</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Process memories at your pace</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-white">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <Lock className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle className="text-2xl">Safety & Stabilization</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Before processing trauma, we ensure you have:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Grounding techniques</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Emotional regulation skills</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Safety planning</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Coping strategies</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-white">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle className="text-2xl">Somatic & Mindfulness</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Address trauma stored in the body through:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Body awareness techniques</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Mindfulness practices</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Breathing exercises</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Nervous system regulation</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Safe Space */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <Lock className="w-16 h-16 text-emerald-600 mx-auto mb-6" />
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            A Safe Space for Your Healing
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            Healing from trauma requires a safe, trusting therapeutic relationship. I provide a 
            non-judgmental, confidential environment where you can share your story at your own pace. 
            You are in control of your healing journey, and I'm here to support you with compassion, 
            expertise, and unwavering respect for your experience.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div>
              <Heart className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
              <h3 className="font-bold text-xl text-gray-900 mb-2">Compassionate</h3>
              <p className="text-gray-600">Non-judgmental support every step</p>
            </div>
            <div>
              <Shield className="w-12 h-12 text-teal-600 mx-auto mb-4" />
              <h3 className="font-bold text-xl text-gray-900 mb-2">Safe</h3>
              <p className="text-gray-600">Confidential, secure environment</p>
            </div>
            <div>
              <Lock className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="font-bold text-xl text-gray-900 mb-2">Your Pace</h3>
              <p className="text-gray-600">You control the healing process</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-100 text-purple-800">Common Questions</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trauma & PTSD Therapy FAQs
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem 
              value="item-1"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-emerald-600 transition-colors">
                Will I have to relive my trauma in therapy?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                No, you won't be forced to relive traumatic experiences. While processing trauma is part of healing, 
                we do this safely and at your pace. We'll first establish coping skills and ensure you feel safe 
                before addressing difficult memories. You're always in control of what we discuss and when.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-2"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-emerald-600 transition-colors">
                How long does trauma therapy take?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                The duration varies based on the nature and severity of the trauma, as well as individual healing 
                processes. Some people experience significant relief in 12-16 sessions, while complex trauma may 
                require longer-term therapy. Healing isn't linear, and we'll work at a pace that feels right for you.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-3"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-emerald-600 transition-colors">
                Is it too late to address trauma that happened years ago?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                It's never too late. Trauma can affect you for years, even decades, but the brain has remarkable 
                capacity for healing at any age. Many people successfully process old trauma and experience 
                profound relief and life improvements, regardless of how long ago the trauma occurred.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-4"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-emerald-600 transition-colors">
                Can trauma therapy be done through telehealth in Hawaii?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Yes. Telehealth trauma therapy is effective and widely used. Many clients find the comfort of 
                their home environment helpful for trauma work. All evidence-based trauma treatments can be 
                adapted for telehealth, and I'm experienced in providing trauma-informed care virtually throughout 
                Hawaii.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-5"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-emerald-600 transition-colors">
                What if I don't remember all the details of my trauma?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                That's completely normal. Memory gaps are common with trauma. You don't need to remember every 
                detail to heal. Trauma therapy focuses on addressing the symptoms and their impact on your current 
                life, not necessarily recovering every memory. We work with whatever you do remember and how the 
                trauma affects you now.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-6"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-emerald-600 transition-colors">
                Do I need to confront the person who traumatized me?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                No, confrontation is not necessary for healing. Trauma therapy focuses on your healing and recovery, 
                not on others' involvement. Whether or not to have any contact with those who caused harm is 
                entirely your choice, and we can explore what feels right and safe for you.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            You Don't Have to Carry This Alone
          </h2>
          <p className="text-xl mb-8 opacity-95">
            Healing from trauma is possible. Take the first step toward reclaiming your life today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold transition-all duration-200 shadow-xl"
            >
              Start Your Healing Journey
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/resources')}
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold transition-all duration-200"
            >
              Trauma Resources
            </Button>
          </div>
          <p className="mt-6 text-sm opacity-80">
            Trauma-informed care • Confidential sessions • Telehealth available
          </p>
        </div>
      </section>
    </div>
  );
};
